import { useState } from "react";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFirestore, collection, addDoc, writeBatch, doc, getDocs, query, where, limit } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import firebaseApp from "../../../firebase/credenciales";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import axios from "axios";
import { FUNCTION_URLS } from "../../../config/functionUrls";

export const useDocumentProcessor = (agentType) => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const db = getFirestore(firebaseApp);
  const auth = getAuth(firebaseApp);

  const getAuthHeaders = async (apiKey = "") => {
    const trimmedApiKey = String(apiKey || "").trim();
    if (trimmedApiKey) {
      return {
        "x-api-key": trimmedApiKey,
      };
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("No hay sesión activa en Firebase. Inicia sesión para continuar.");
    }
    const token = await currentUser.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  // Notificaciones
  const showNotification = (title, message, icon = "success") => {
    Swal.fire({ title, text: message, icon, timer: 3000, toast: false, position: "center", showConfirmButton: false });
  };

  // Subir archivo a Firebase Storage
  const uploadToFirebase = async (file, ruc = 'general') => {
    const storage = getStorage(firebaseApp);
    const fileName = `ai-documents/${ruc}/${agentType}/${uuidv4()}-${file.name}`;
    const fileRef = ref(storage, fileName);
    await uploadBytes(fileRef, file);
    const downloadUrl = await getDownloadURL(fileRef);
    return { url: downloadUrl, storagePath: fileName };
  };

  // Indexar fragmentos en Firestore (COLECCIONES ESPECÍFICAS POR AGENTE)
  const indexFragmentsToFirestore = async (storagePath, fragments, metadata, agentSpecificFields = {}, options = {}) => {
    // Definir colecciones basadas en agentType.
    // Lexi escribe solo en colecciones core para evitar duplicidad y crecimiento innecesario.
    const writeTargetsByAgent = {
      hiro: [
        { name: "legacy", documents: "hiro_documentos", fragments: "hiro_fragmentos" },
      ],
      lexi: [
        { name: "core", documents: "lexi_documents", fragments: "lexi_chunks" },
      ],
      vexa: [
        { name: "core", documents: "vexa_documents", fragments: "vexa_chunks" },
      ],
    };

    const writeTargets = writeTargetsByAgent[agentType] || writeTargetsByAgent.lexi;
    
    const documentId = metadata.document_id || metadata.documentId;

    const documentData = {
      document_id: documentId,
      title: metadata.title,
      description: metadata.description,
      source: "PDF",
      source_filename: metadata.sourceFilename,
      storage_path: storagePath,  // Agregado: referencia al archivo en Firebase Storage
      creation_date: new Date().toISOString(),
      page_count: metadata.pageCount,
      fragment_count: fragments.length,
      status: "active",
      agent_type: agentType,
      ruc: metadata.ruc,
      tenantId: metadata.tenantId || null,
      companyId: metadata.companyId || null,
      productMode: metadata.productMode || null,
      candidate_name: metadata.candidate_name || null,  // Agregado para Hiro
      required_position: metadata.required_position || null,  // Agregado para Hiro
      seniority_level: metadata.seniority_level || null,  // Agregado para Hiro
      document_type: metadata.document_type || null,  // Agregado para Hiro
      category: metadata.category || null,  // Agregado para Hiro
      ...agentSpecificFields.document || {}
    };

    for (const target of writeTargets) {
      const payload = {
        ...documentData,
        source_collection: target.documents,
      };
      await addDoc(collection(db, target.documents), payload);
    }

    const batchSize = 20;
    const cleanupExistingChunks = !!options.cleanupExistingChunks;

    const isCleanableAgent = agentType === "lexi" || agentType === "vexa";
    const chunksCollectionName = agentType === "vexa" ? "vexa_chunks" : "lexi_chunks";

    if (isCleanableAgent && cleanupExistingChunks && documentId) {
      // Limpieza segura previa: elimina chunks anteriores del mismo document_id y contexto.
      const oldChunksQuery = query(
        collection(db, chunksCollectionName),
        where("document_id", "==", documentId),
        limit(10000)
      );
      const oldChunksSnapshot = await getDocs(oldChunksQuery);
      if (!oldChunksSnapshot.empty) {
        let cleanupBatch = writeBatch(db);
        let cleanupCount = 0;

        for (const chunkDoc of oldChunksSnapshot.docs) {
          const data = chunkDoc.data() || {};
          const sameTenant = !metadata.tenantId || !data.tenantId || data.tenantId === metadata.tenantId;
          const sameRuc = !metadata.ruc || !data.ruc || data.ruc === metadata.ruc;
          const sameMode = !metadata.productMode || !data.productMode || data.productMode === metadata.productMode;
          const sameCompany = !metadata.companyId || !data.companyId || data.companyId === metadata.companyId;

          if (!(sameTenant && sameRuc && sameMode && sameCompany)) continue;

          cleanupBatch.delete(chunkDoc.ref);
          cleanupCount++;

          if (cleanupCount % batchSize === 0) {
            await cleanupBatch.commit();
            cleanupBatch = writeBatch(db);
          }
        }

        if (cleanupCount % batchSize !== 0) {
          await cleanupBatch.commit();
        }
      }
    }

    let batch = writeBatch(db);
    let batchCount = 0;

    for (const fragment of fragments) {
      for (const target of writeTargets) {
        const fragmentRef = doc(collection(db, target.fragments));
        const fragmentData = {
          fragment_id: fragment.id,
          document_id: documentId,
          agent_type: agentType,
          page: fragment.page,
          text: fragment.text,
          char_count: fragment.char_count,
          structure_type: fragment.structure_type,
          structure_header: fragment.structure_header,
          article_number: fragment.article_number || fragment.articleNumber || null,
          article_title: fragment.article_title || fragment.articleTitle || null,
          citation_label: fragment.citation_label || fragment.citationLabel || null,
          page_start: fragment.page_start || fragment.pageStart || null,
          page_end: fragment.page_end || fragment.pageEnd || null,
          chunking_version: fragment.chunking_version || metadata.chunking_version || null,
          embedding_provider: fragment.embedding_provider || metadata.embedding_provider || null,
          embedding_dimensions: fragment.embedding_dimensions || metadata.embedding_dimensions || null,
          embedding_version: fragment.embedding_version || metadata.embedding_version || null,
          extraction_date: new Date().toISOString(),
          ruc: metadata.ruc,
          tenantId: metadata.tenantId || null,
          companyId: metadata.companyId || null,
          productMode: metadata.productMode || null,
          document_type: metadata.document_type || null,
          category: metadata.category || null,
          candidate_name: fragment.candidate_name || null,  // Agregado para Hiro
          required_position: fragment.required_position || null,  // Agregado para Hiro
          seniority_level: fragment.seniority_level || null,  // Agregado para Hiro
          status: fragment.status || 'active',  // Agregado para Hiro
          cv_section: fragment.cv_section || 'general',  // Agregado para Hiro
          relevance_score: fragment.relevance_score || 0.5,  // Agregado para Hiro
          source_collection: target.fragments,
          ...agentSpecificFields.fragment || {},
          ...fragment.agentSpecificFields || {}
        };

        batch.set(fragmentRef, fragmentData);
        batchCount++;
        if (batchCount >= batchSize) {
          try {
            await batch.commit();
            console.log(`Batch de ${batchCount} fragmentos guardado exitosamente.`);
          } catch (error) {
            console.error(`Error guardando batch: ${error.message}`);
            throw error;
          }
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
    }

    if (batchCount > 0) {
      try {
        await batch.commit();
        console.log(`Último batch de ${batchCount} fragmentos guardado exitosamente.`);
      } catch (error) {
        console.error(`Error guardando último batch: ${error.message}`);
        throw error;
      }
    }
    return true;
  };
  
  // Procesar embeddings con URLs específicas por agente
  const processEmbeddings = async (document_id, documentType, category, ruc, context = {}) => {
    // URLs específicas por agente
    const functionUrls = {
      hiro: FUNCTION_URLS.hiroProcessFragmentEmbeddings,
      lexi: FUNCTION_URLS.lexiProcessFragmentEmbeddings,
      vexa: FUNCTION_URLS.lexiProcessFragmentEmbeddings
    };

    const functionUrl = functionUrls[agentType] || functionUrls.lexi; // Fallback a lexi si no se reconoce

    let continueProcessing = true;
    let totalProcessed = 0;
    const batchSize = 50;

    while (continueProcessing) {
      try {
        const requestBody = {
          documentId: document_id,
          document_id,
          batchSize,
          ruc,
          documentType,
          category,
          tenantId: context.tenantId,
          companyId: context.companyId,
          productMode: context.productMode,
        };

        const authHeaders = await getAuthHeaders(context.apiKey);

        const response = await axios.post(functionUrl, requestBody, {
          headers: authHeaders,
        });

        if (response.data.success) {
          if (response.data.processed > 0) {
            totalProcessed += response.data.processed;
          } else {
            continueProcessing = false;
          }
        } else {
          // Si no se encontraron fragmentos y ya procesamos algunos, considerar éxito
          if (response.data.message === "No se encontraron fragmentos pendientes de procesar" && totalProcessed > 0) {
            continueProcessing = false;
          } else {
            throw new Error(response.data.message);
          }
        }
      } catch (error) {
        // Si es el primer batch y no hay fragmentos, relanzar error
        if (totalProcessed === 0) {
          throw error;
        } else {
          // Si ya procesamos algunos, detener
          continueProcessing = false;
        }
      }
    }

    return { processed: totalProcessed };
  };

  return {
    processing,
    setProcessing,
    progress,
    setProgress,
    statusMessage,
    setStatusMessage,
    error,
    setError,
    success,
    setSuccess,
    uploadToFirebase,
    indexFragmentsToFirestore,
    processEmbeddings,
    showNotification
  };
};
